export const TERMS_AND_CONDITIONS_TITLE = "Terms of Service"

export const TERMS_AND_CONDITIONS_LAST_UPDATED = "May 22nd, 2026"

export const TERMS_AND_CONDITIONS_INTRO = [
  'Welcome to Aegiss. These Terms of Service ("Terms") govern your access to and use of our AI-powered tax research platform (the "Service").',
  "BY ACCESSING OR USING THE SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE, DO NOT USE THE SERVICE.",
] as const

export type TermsSection = {
  heading: string
  paragraphs?: readonly string[]
  bullets?: readonly string[]
}

export const TERMS_AND_CONDITIONS_SECTIONS: readonly TermsSection[] = [
  {
    heading: "1. Scope of Agreement and Enterprise Customers",
    paragraphs: [
      "Aegiss provides an AI-driven platform specifically designed for enterprise tax research.",
      "Enterprise Precedence: If you are using the Service on behalf of an organization that has entered into a separate written agreement with Aegiss (e.g., a Master Service Agreement or \"MSA\"), that agreement shall take precedence over these Terms in the event of a conflict.",
      "Eligibility: You must be at least 18 years old and have the legal authority to bind your organization to these Terms.",
    ],
  },
  {
    heading: "2. No Tax or Professional Advice",
    paragraphs: [
      "Aegiss is a technology provider, not a law firm or a firm of chartered accountants. The Service provides AI-generated summaries and research based on tax legislation and case law. All outputs are for informational and research purposes only.",
      "Verification: You are solely responsible for verifying the accuracy and applicability of any output generated by the Service.",
      "No Reliance: The Service does not constitute legal, tax, or financial advice. You should consult with a qualified professional before making any decisions based on information retrieved through the Service. You acknowledge that the Service utilizes artificial intelligence and machine learning technologies. Due to the nature of these technologies, the Service may occasionally generate outputs that are inaccurate, incomplete, or outdated. Outputs should be treated as a starting point for research, not a definitive legal source.",
    ],
  },
  {
    heading: "3. Acceptable Use Policy (AUP)",
    paragraphs: [
      "To ensure the security and integrity of our platform, you agree NOT to:",
    ],
    bullets: [
      "Use the Service for any unlawful purpose or in violation of Scottish law.",
      "Attempt to bypass, circumvent, or disable any security features.",
      "Reverse engineer, decompile, or attempt to extract the source code or underlying algorithms of the Service.",
      "Use automated systems (bots, spiders, or scrapers) to harvest data from the Site.",
      "Upload or transmit any viruses, malware, or other malicious code.",
      'Use the Service to generate "spam" or misleading content.',
    ],
  },
  {
    heading: "4. Confidentiality and Non-Disclosure",
    paragraphs: [
      'Confidential Information: By participating in this MVP/early-access release, you may gain access to non-public information, including but not limited to the Service\'s user interface, features, performance data, documentation, and underlying AI capabilities ("Confidential Information").',
      "Restrictions: You agree to hold all Confidential Information in strict confidence and not to disclose it to any third party (especially competitors of Aegiss) without our prior written consent. You may use Confidential Information solely for the purpose of evaluating and using the Service as permitted under these Terms.",
    ],
  },
  {
    heading: "5. Intellectual Property",
    paragraphs: [
      "Our Property: Aegiss and its licensors own all rights, title, and interest in the Service, including all software, logos, and branding.",
      'Your Content: You retain ownership of the data you input into the Service ("Customer Data"). You grant Aegiss a limited, worldwide license to process Customer Data solely to provide and improve the Service to you.',
      "Feedback: If you provide any suggestions, feedback, or ideas regarding the Service ('Feedback'), you grant Aegiss a perpetual, irrevocable, royalty-free, worldwide license to use and incorporate such Feedback into our products and services without restriction or compensation to you.",
    ],
  },
  {
    heading: "6. Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by law:",
      "Aegiss shall not be liable for any indirect, incidental, special, or consequential damages.",
      "Our total liability for any claim arising out of these Terms shall not exceed the amount paid by you to Aegiss in the 12 months preceding the claim.",
      'Disclaimer: THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.',
    ],
  },
  {
    heading: "7. Termination",
    paragraphs: [
      "We reserve the right to suspend or terminate your access to the Service at our sole discretion, without notice, if we believe you have breached these Terms or pose a security risk to the platform.",
    ],
  },
  {
    heading: "8. Governing Law and Jurisdiction",
    paragraphs: [
      "These Terms and any dispute or claim arising out of or in connection with them (including non-contractual disputes) shall be governed by and construed in accordance with the laws of Scotland.",
      "The courts of Scotland shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with these Terms.",
    ],
  },
  {
    heading: "9. Changes to Terms",
    paragraphs: [
      'We may update these Terms from time to time to reflect changes in our legal obligations or Service features. We will notify you of material changes by updating the "Last Updated" date at the top of this page.',
    ],
  },
  {
    heading: "Contact Information",
    paragraphs: [
      "For questions regarding these Terms, please contact:",
      "Legal & Compliance Team",
      "Email: legal@aegiss.ai",
    ],
  },
]
